import { ApolloLink } from "@apollo/client";
import type {
  InMemoryCache,
  NormalizedCacheObject,
  StoreObject,
} from "@apollo/client";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isRef(value: Record<string, unknown>): value is { __ref: string } {
  return typeof value.__ref === "string";
}

type AccountCacheLinkOptions = {
  /**
   * Root-level field names that are NOT account-scoped (e.g. @allowAnonymous
   * fields from the schema). Everything else is treated as account-bound.
   */
  anonymousFields: ReadonlySet<string>;
  /** The InMemoryCache instance used by the Apollo client. */
  cache: InMemoryCache;
  /** Whether per-account cache swapping is enabled. Defaults to true. */
  enabled?: boolean;
  /** Maximum number of account snapshots to keep in memory. Defaults to 5. */
  maxSnapshots?: number;
};

const DEFAULT_SNAPSHOT_KEY = "default";
const DEFAULT_MAX_SNAPSHOTS = 5;

/**
 * An Apollo Link that provides per-account cache isolation.
 *
 * Fields are classified using a set of anonymous (non-account-scoped) field
 * names extracted from the schema's `@allowAnonymous` directive at build time.
 * Any root query field **not** in that set is treated as account-bound.
 *
 * As a **cache manager**, it exposes `switchAccount()` which swaps the
 * InMemoryCache data in-place — saving a snapshot of the outgoing account
 * and restoring the incoming account's snapshot (or seeding shared fields
 * for first visits).
 *
 * Usage:
 * ```ts
 * const cache = new InMemoryCache({ ... });
 * const accountCacheLink = new AccountCacheLink({
 *   anonymousFields: ANONYMOUS_FIELDS, // from src/gql/anonymous-fields.ts
 *   cache,
 * });
 * // Add to the Apollo link chain
 * ApolloLink.from([accountCacheLink, ...otherLinks])
 * // On account switch
 * accountCacheLink.switchAccount(previousId, nextId);
 * ```
 */
class AccountCacheLink extends ApolloLink {
  /** Serialized cache snapshots keyed by accountId, with LRU eviction. */
  readonly snapshots = new Map<string, NormalizedCacheObject>();

  /** The current account ID, kept in sync by switchAccount. */
  currentAccountId: string | undefined;

  private readonly anonymousFields: ReadonlySet<string>;
  private readonly cache: InMemoryCache;
  private readonly enabled: boolean;
  private readonly maxSnapshots: number;

  constructor(options: AccountCacheLinkOptions) {
    const maxSnapshots = options.maxSnapshots ?? DEFAULT_MAX_SNAPSHOTS;

    super((operation, forward) => forward(operation));
    this.anonymousFields = options.anonymousFields;
    this.cache = options.cache;
    this.enabled = options.enabled ?? true;
    this.maxSnapshots = maxSnapshots;
  }

  /**
   * Returns true if a cache key represents an account-bound field.
   * A field is account-bound if it is NOT in the anonymousFields set.
   */
  isAccountBoundField(cacheKey: string) {
    const fieldName = /^[^(]+/.exec(cacheKey)?.[0] ?? cacheKey;
    return !this.anonymousFields.has(fieldName);
  }

  /**
   * Swaps the cache data in-place on a single InMemoryCache.
   * 1. Saves the current cache contents under the previous account key.
   * 2. Evicts all entries from the live cache.
   * 3. Restores the target account's snapshot if previously visited.
   * 4. Carries over shared (non-account-bound) root query fields.
   */
  switchAccount(
    previousAccountId: string | undefined,
    nextAccountId: string | undefined
  ) {
    this.currentAccountId = nextAccountId;
    if (!this.enabled) {
      // When cache swapping is disabled, just evict account-bound fields
      // so the previous account's data doesn't leak into the next.
      const { cache } = this;
      const rootQuery = cache.extract().ROOT_QUERY;
      if (rootQuery) {
        cache.batch({
          update: (c) => {
            const evicted = new Set<string>();
            for (const cacheKey of Object.keys(rootQuery)) {
              if (this.isAccountBoundField(cacheKey)) {
                const fieldName = /^[^(]+/.exec(cacheKey)?.[0] ?? cacheKey;
                if (!evicted.has(fieldName)) {
                  evicted.add(fieldName);
                  c.evict({ id: "ROOT_QUERY", fieldName });
                }
              }
            }
            c.gc();
          },
        });
      }
      return;
    }

    const { cache } = this;
    const currentData = cache.extract();
    const prevKey = previousAccountId ?? DEFAULT_SNAPSHOT_KEY;
    const nextKey = nextAccountId ?? DEFAULT_SNAPSHOT_KEY;

    this.setSnapshot(prevKey, currentData);

    cache.batch({
      update: (c) => {
        for (const entityId of Object.keys(currentData)) {
          c.evict({ id: entityId });
        }
        c.gc();
      },
    });

    const targetSnapshot = this.snapshots.get(nextKey);
    if (targetSnapshot) {
      cache.restore(targetSnapshot);
    }

    this.carryOverSharedFields(currentData);
  }

  private setSnapshot(key: string, data: NormalizedCacheObject) {
    this.snapshots.delete(key);
    this.snapshots.set(key, data);

    if (this.snapshots.size > this.maxSnapshots) {
      const oldest = this.snapshots.keys().next().value;
      if (oldest !== undefined) {
        this.snapshots.delete(oldest);
      }
    }
  }

  private carryOverSharedFields(previousData: NormalizedCacheObject) {
    const { cache } = this;
    const restoredData = cache.extract();
    const previousRootQuery = previousData.ROOT_QUERY;
    const restoredRootQuery = restoredData.ROOT_QUERY;

    if (!previousRootQuery) return;

    const missingFields: StoreObject = {};
    for (const cacheKey of Object.keys(previousRootQuery)) {
      if (cacheKey === "__typename") continue;
      if (
        !this.isAccountBoundField(cacheKey) &&
        previousRootQuery[cacheKey] !== undefined &&
        restoredRootQuery?.[cacheKey] === undefined
      ) {
        missingFields[cacheKey] = previousRootQuery[cacheKey];
      }
    }

    if (Object.keys(missingFields).length === 0) return;

    const merged: NormalizedCacheObject = { ...restoredData };
    merged.ROOT_QUERY = {
      __typename: "Query",
      ...restoredRootQuery,
      ...missingFields,
    };

    const reachable = new Set<string>();
    this.collectReachableEntities(missingFields, previousData, reachable);

    for (const entityId of reachable) {
      if (!(entityId in merged)) {
        merged[entityId] = previousData[entityId];
      }
    }

    cache.restore(merged);
  }

  /**
   * Recursively collects entity IDs reachable from a value via __ref links.
   */
  private collectReachableEntities(
    value: unknown,
    data: NormalizedCacheObject,
    visited: Set<string>
  ) {
    if (!isRecord(value)) return;

    if (isRef(value)) {
      const ref = value.__ref;
      if (!visited.has(ref) && ref in data) {
        visited.add(ref);
        this.collectReachableEntities(data[ref], data, visited);
      }
      return;
    }

    for (const val of Object.values(value)) {
      this.collectReachableEntities(val, data, visited);
    }
  }
}

export { AccountCacheLink };
export type { AccountCacheLinkOptions };
